import { useState } from "react";
import { createPortal } from "react-dom";
import { FaCreditCard } from "react-icons/fa";
import PaymentModal from "Components/PaymentModal";
import Button from "Components/UI/Button";
import Select from "Components/UI/Select";
import DetailSection from "Components/PatentDesign/DetailSection";
import { disabledStyle, pdStyles } from "Components/PatentDesign/styles";
import { updateAddedPaymentServiceType } from "JS/API";
import { paidStatusList, paidTypeList } from "JS/array";
import { designTokens } from "utils/designSystem";
import {
  ERROR_MESSAGE,
  QUOTE_AUTO_STATUSES,
  SERVICE_TYPE,
  SERVICE_TYPE_ORDER,
  UNASSIGNED_LABEL,
  formatDate,
  formatPrice,
  isPaymentRequestable,
} from "utils/patentDesign";

const HEADERS = ["요청일", "유형", "사유", "금액", "결제 상태", "기한", "결제일", "관리"];

const PaymentSection = ({ detail, onReload, onAlert, onError }) => {
  const [payModal, setPayModal] = useState(false);
  const [editPayment, setEditPayment] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const payments = detail.payments || [];
  const canRequestPayment = isPaymentRequestable(detail.status);
  const keepsStatus = canRequestPayment && !QUOTE_AUTO_STATUSES.includes(detail.status);

  const handleServiceTypeChange = async (payment, serviceType) => {
    if (!serviceType || serviceType === payment.service_type || updatingId) return;
    setUpdatingId(payment.added_id);
    try {
      const result = await updateAddedPaymentServiceType(payment.added_id, serviceType);
      if (result && typeof result === "object") {
        onAlert("결제 유형을 변경했습니다.");
        onReload();
      } else if (ERROR_MESSAGE[result]) {
        onAlert(ERROR_MESSAGE[result]);
      } else {
        onError(result);
      }
    } catch {
      onError();
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <DetailSection
      title="견적 · 결제"
      actions={
        <Button
          variant="success"
          icon={<FaCreditCard />}
          onClick={() => setPayModal(true)}
          disabled={!canRequestPayment}
          style={disabledStyle(!canRequestPayment)}
        >
          견적 · 결제 요청
        </Button>
      }
    >
      {!canRequestPayment && (
        <p style={{ ...pdStyles.hint, marginBottom: designTokens.spacing.md }}>
          등록 완료 · 거절 · 취소된 신청에는 결제를 요청할 수 없어요
        </p>
      )}
      {keepsStatus && (
        <p style={{ ...pdStyles.hint, marginBottom: designTokens.spacing.md }}>
          지금 요청하는 결제는 추가 비용으로 처리돼 진행 단계가 바뀌지 않아요
        </p>
      )}

      {payments.length ? (
        <div style={pdStyles.tableWrapper}>
          <table style={pdStyles.table}>
            <thead>
              <tr>
                {HEADERS.map((label) => (
                  <th key={label} style={pdStyles.th}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.added_id}>
                  <td style={{ ...pdStyles.td, ...pdStyles.nowrap }}>
                    {formatDate(payment.created_at)}
                  </td>
                  <td style={pdStyles.td}>
                    <Select
                      aria-label={`결제 ${payment.added_id} 유형`}
                      value={payment.service_type || ""}
                      disabled={Boolean(updatingId)}
                      onChange={(e) => handleServiceTypeChange(payment, e.target.value)}
                      style={{ width: "110px", padding: "6px 8px" }}
                    >
                      {!payment.service_type && (
                        <option value="" disabled>
                          {UNASSIGNED_LABEL}
                        </option>
                      )}
                      {SERVICE_TYPE_ORDER.map((type) => (
                        <option key={type} value={type}>
                          {SERVICE_TYPE[type]}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td style={pdStyles.td}>
                    <div>{payment.paid_reason}</div>
                    {payment.paid_reason_detail && (
                      <div style={pdStyles.muted}>{payment.paid_reason_detail}</div>
                    )}
                  </td>
                  <td style={pdStyles.td}>{formatPrice(payment.merchant_total_price)}</td>
                  <td style={{ ...pdStyles.td, ...pdStyles.nowrap }}>
                    <div>{paidStatusList[payment.paid_status] || payment.paid_status || "-"}</div>
                    {payment.paid_method && (
                      <div style={pdStyles.muted}>
                        {paidTypeList[payment.paid_method] || payment.paid_method}
                      </div>
                    )}
                  </td>
                  <td style={pdStyles.td}>{formatDate(payment.ended_at)}</td>
                  <td style={pdStyles.td}>{formatDate(payment.paid_at)}</td>
                  <td style={pdStyles.td}>
                    <Button
                      variant="secondary"
                      aria-label={`결제 ${payment.added_id} 수정`}
                      onClick={() => setEditPayment(payment)}
                      style={{ padding: "6px 12px", whiteSpace: "nowrap" }}
                    >
                      수정
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={pdStyles.muted}>아직 없음</p>
      )}

      {payModal &&
        createPortal(
          <PaymentModal
            setPayModal={setPayModal}
            userPk={detail.user?.user_pk}
            patentDesignRequestPk={detail.id}
            patentDesignRequestType={detail.request_type}
            onSuccess={onReload}
          />,
          document.body
        )}
      {editPayment &&
        createPortal(
          <PaymentModal
            setPayModal={() => setEditPayment(null)}
            userPk={detail.user?.user_pk}
            applyPaymentPk={editPayment.added_id}
            editData={{ ...editPayment, add_payment_pk: editPayment.added_id }}
            setEditData={setEditPayment}
            taxInfo={editPayment}
            onSuccess={onReload}
          />,
          document.body
        )}
    </DetailSection>
  );
};

export default PaymentSection;
